import React from 'react'
// hooks
import { useChartView } from 'hooks/useChartView'
// types
import { ChartConfig } from 'types/charts'
import { ChartAggregate } from 'types/graphql'
// views
import { ChartBody, ChartMessage } from '../../views'
// utils
import { getChartErrorMessage } from 'utils/charts/errors'

// echarts is the biggest part of a chart: it loads only when a chart is on screen
const EChart = React.lazy(() => import('components/UI/EChart'))

interface Props {
  config: ChartConfig | null
  title?: string
  // A saved chart shows the viewer's snapshot; without it the config is previewed live
  chartId?: string
}

const ChartView: React.FC<Props> = ({ config, title, chartId }) => {
  const { option, loading, error, isReady, isEmpty, hasNoValues, isPending } = useChartView(config, {
    chartId,
    title
  })

  const getMessage = () => {
    if (
      config?.query.riskTemplateId &&
      config.query.aggregate !== ChartAggregate.Count &&
      !config.query.aggregateFieldId
    ) {
      return 'Pick the number field to aggregate in the widget settings'
    }
    // Every chart type takes at least one group-by field (GROUP_BY_COUNTS)
    if (config?.query.riskTemplateId && !config.query.groupBy.length) {
      return 'Pick what to group the records by in the widget settings'
    }
    if (!isReady) {
      return 'Pick a form and what to count in the widget settings'
    }
    if (error) {
      return getChartErrorMessage(error)
    }
    if (isEmpty) {
      return 'No records match this chart'
    }
    if (hasNoValues) {
      return 'The matching records have no value in the number field'
    }
    return null
  }

  const message = getMessage()

  return (
    <ChartBody>
      {message ? (
        <ChartMessage>{message}</ChartMessage>
      ) : (
        <React.Suspense fallback={null}>
          <EChart option={option} loading={loading || isPending} />
        </React.Suspense>
      )}
    </ChartBody>
  )
}

export default ChartView
